import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  EditableOutlined,
  HistoryUniversal,
  MoreInfoOutlined,
  OpenInBrowserOutlined,
  SubtractUniversal,
} from '@duxton-web/icons';
import scribe from '@grab-x/scribe-web-sdk';
import { useLocation } from 'react-router-dom';

import Service, { VariableInfo } from '../../../lib/App';
import { useAppNavigate } from '@/utils/navigation';

import './Configurations.css';

import {
  Button,
  Card,
  ConfigProvider,
  Flex,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { debounce } from 'lodash';
import { useLocalStorage } from 'react-use';

import {
  VARIABLES_PARAM_MINEONLY,
  VARIABLES_PARAM_ORDER,
  VARIABLES_PARAM_OWNER,
  VARIABLES_PARAM_PAGENO_KEY,
  VARIABLES_PARAM_PAGESIZE_KEY,
  VARIABLES_PARAM_SEARCH_KEY,
  VARIABLES_PARAM_SORTBY,
  VARIABLES_PARAM_STATE,
  VARIABLES_PARAM_STATES,
  VARIABLES_TAB_VIEW,
  VariableState,
} from '../../../constants';
import rocketIconSvg from './components/rocket-icon.svg';
import { getConfigurationsFilters } from './const';
import {
  AccessControlWrapper,
  DashboardTable,
  DataTypeIcon,
  ObjectDetailPage,
  ObjectInfo,
  SecondaryText,
  Text,
} from '@/common/components';
import { DashboardColumn } from '@/common/components/DashboardTable';
import { OBJECTDETAILPAGE_OBJECTTYPE } from '@/common/components/ObjectDetailPage/ObjectDetailPage';
import { isProduction } from '@/common/environment';
import { newVariablesEvents } from '@/common/Scribe/EventsList';
import Announcements from '@/features/announcement';
import VariableSettings from '@/features/variable/settings';
import { ANNOUNCEMENTS } from '@/pages/MainLayout/announcements';
import VariableStateTag from '@/pages/Variable/components/VariableStateTag/VariableStateTag';
import { VARIABLE_PAGE_SIZE } from '@/store/state/variables/actions';
import { UserAction } from '@/utils/accessControl';
import {
  formatUTCStringToDateTimeString,
  getTimezoneDisplay,
} from '@/utils/dateTime';
import {
  getExternalUrl,
  getUnsupportedService,
  UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY_MAP,
} from '@/utils/externalService';
import { deepClone } from '@/utils/object';
import {
  extractFiltersFromSearchParams,
  extractParamValueByName,
} from '@/utils/url';

const GO_ZERO_DATE = '0001-01-01T00:00:00Z';
const EMPTY_TIMESTAMP = '-';

const enum ConfigurationTabView {
  ALL = 'all',
  MY = 'my',
}

const Configurations = () => {
  const { navigateTo } = useAppNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchVal, setSearchVal] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(VARIABLE_PAGE_SIZE);
  const [sortBy, setSortBy] = useState('');
  const [order, setOrder] = useState(null);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableCustomization, setTableCustomization] = useLocalStorage(
    `product-configurations_tableCustomizer`,
    null,
  );
  const [tabView, setTabView] = useState<string>(ConfigurationTabView.ALL);

  const EXTERNAL_PORTAL_TOOLTIP = (url) =>
    `Clicking here will open ${url} in a new tab`;

  const fetchData = async (
    searchTerm: string = '',
    filters: { [key: string]: any } = {},
    pageNumber: number = 1,
    pageSize: number = VARIABLE_PAGE_SIZE,
    sortBy = '',
    order = '',
  ) => {
    setLoading(true);

    try {
      const filterClone = JSON.parse(JSON.stringify(filters));

      const res = await Service.planner.getProductConfigrations({
        search_term: searchTerm,
        variable_filters: filterClone,
        limit: pageSize,
        offset: (pageNumber - 1) * pageSize,
        sortBy: sortBy,
        ordering: order,
      });

      if (res?.data) {
        setTotal(res.data?.total || 0);
        setData(res?.data?.items);
      } else {
        setTotal(0);
        setData([]);
      }

      setLoading(false);
    } catch (e) {
      setTotal(0);
      setData([]);
      setLoading(false);
    }
  };

  const defaultStates = [VariableState.ACTIVE, VariableState.INACTIVE];

  function matchingDefaultStates(defaultStatesArr: string[]): boolean {
    if (defaultStatesArr.length !== defaultStates.length) return false;

    let isMatching = true;

    defaultStatesArr.forEach((defaultState) => {
      if (!defaultStates.includes(defaultState as VariableState)) {
        isMatching = false;
        return false;
      }
    });

    return isMatching;
  }

  /**
   * return the filters need to be used in the filter drawer for variables listing
   * */
  function parseTableFilters(
    filters: { [key: string]: unknown },
    configurationViewTab: string,
  ) {
    if (!filters) return {};

    const filtersClone = deepClone(filters);

    if (
      !filtersClone?.[VARIABLES_PARAM_STATE] &&
      !filtersClone?.[VARIABLES_PARAM_STATES]
    ) {
      filtersClone[VARIABLES_PARAM_STATE] = defaultStates;

      delete filtersClone[VARIABLES_PARAM_STATES];
    }

    if (filtersClone?.[VARIABLES_PARAM_STATE])
      filtersClone[VARIABLES_PARAM_STATE] =
        filtersClone[VARIABLES_PARAM_STATE].toString().split(',');

    if (filtersClone?.[VARIABLES_PARAM_STATES])
      filtersClone[VARIABLES_PARAM_STATES] =
        filtersClone[VARIABLES_PARAM_STATES].toString().split(',');

    if (configurationViewTab === ConfigurationTabView.MY)
      filtersClone[VARIABLES_PARAM_MINEONLY] = true;

    return filtersClone;
  }

  /**
   * return the filters need to be used for querying the variables.
   * if filters contains defaultState, translate it into state
   * */
  function parseSearchFilters(
    filters: { [key: string]: any },
    configurationViewTab: string,
  ) {
    if (!filters) return {};

    const filtersClone = deepClone(filters);

    if (filters?.[VARIABLES_PARAM_STATE]) {
      filtersClone[VARIABLES_PARAM_STATES] =
        filters[VARIABLES_PARAM_STATE].toString().split(',');
      delete filtersClone[VARIABLES_PARAM_STATE];
    }

    if (!filters?.states?.length) {
      filtersClone[VARIABLES_PARAM_STATES] = defaultStates;
    } else {
      filtersClone[VARIABLES_PARAM_STATES] = filters.states
        .toString()
        .split(',');
    }

    if (configurationViewTab === ConfigurationTabView.MY)
      filtersClone['mineOnly'] = true;

    return filtersClone;
  }

  const getConfigurations = (configurationViewTab: string) => {
    const searchUrl = location.search;
    const searchVal =
      extractParamValueByName(searchUrl, VARIABLES_PARAM_SEARCH_KEY) || '';
    const blacklist = [
      VARIABLES_PARAM_SEARCH_KEY,
      VARIABLES_PARAM_PAGENO_KEY,
      VARIABLES_PARAM_PAGESIZE_KEY,
      'env',
    ];
    const filters = extractFiltersFromSearchParams(searchUrl, blacklist);
    let pageNo =
      extractParamValueByName(searchUrl, VARIABLES_PARAM_PAGENO_KEY) || 1;
    let pageSize =
      extractParamValueByName(searchUrl, VARIABLES_PARAM_PAGESIZE_KEY) ||
      VARIABLE_PAGE_SIZE;
    const sortBy = extractParamValueByName(searchUrl, VARIABLES_PARAM_SORTBY);
    const order = extractParamValueByName(searchUrl, VARIABLES_PARAM_ORDER);

    if (pageNo) pageNo = parseInt(pageNo, 10);

    if (pageSize) pageSize = parseInt(pageSize, 10);

    const tableFilters = parseTableFilters(filters, configurationViewTab);
    const searchFilters = parseSearchFilters(filters, configurationViewTab);

    setSearchVal(searchVal);
    setPageNumber(pageNo);
    setPageSize(pageSize);
    setSortBy(sortBy);
    setOrder(order);
    setFilters({ ...tableFilters, [VARIABLES_PARAM_SEARCH_KEY]: searchVal });

    fetchData(searchVal, searchFilters, pageNo, pageSize, sortBy, order);
  };

  useEffect(() => {
    const currentTabView = extractParamValueByName(
      location.search,
      VARIABLES_TAB_VIEW,
    );

    getConfigurations(
      currentTabView ? currentTabView : ConfigurationTabView.ALL,
    );

    setTabView(currentTabView ? currentTabView : ConfigurationTabView.ALL);
  }, [location.search]);

  const debouncedFilter = useCallback(
    debounce((name) => navigateTo(name, { replace: true }), 500),
    [navigateTo],
  );

  const updateFormAndURL = (filters: { [key: string]: any }) => {
    const payload = {
      ...filters,
    };

    updateURL(`/t/variables`, payload);
  };

  const updateURL = (pathname, payload) => {
    const searchParams = new URLSearchParams(payload);

    debouncedFilter(`${pathname}${searchParams ? `?${searchParams}` : ''}`);
  };

  const onSearch = (
    searchVal: string,
    filters: { [key: string]: any },
    selectedPageNumber: number,
    selectedPageSize: number,
    _type,
    sortBy,
    order,
  ) => {
    const finalFilters = {
      ...filters,
      ...{
        pageSize: selectedPageSize,
        pageNo: selectedPageNumber,
      },
    };

    if (sortBy) finalFilters[VARIABLES_PARAM_SORTBY] = sortBy;
    else delete finalFilters[VARIABLES_PARAM_SORTBY];

    if (order) finalFilters[VARIABLES_PARAM_ORDER] = order;
    else delete finalFilters[VARIABLES_PARAM_ORDER];

    if (searchVal) finalFilters[VARIABLES_PARAM_SEARCH_KEY] = searchVal;
    else delete finalFilters[VARIABLES_PARAM_SEARCH_KEY];

    if (!filters?.states?.length && !filters?.[VARIABLES_PARAM_STATE]) {
      finalFilters[VARIABLES_PARAM_STATE] = defaultStates;
      delete finalFilters[VARIABLES_PARAM_STATES];
    } else if (
      filters?.defaultState &&
      (filters?.[VARIABLES_PARAM_STATE].length > defaultStates.length ||
        !matchingDefaultStates(filters.defaultState))
    ) {
      finalFilters[VARIABLES_PARAM_STATES] = filters?.[VARIABLES_PARAM_STATE];
      delete finalFilters[VARIABLES_PARAM_STATE];
    }

    updateFormAndURL(finalFilters);
  };

  const transformDate = (date: string) => {
    if (date === GO_ZERO_DATE) return EMPTY_TIMESTAMP;

    return formatUTCStringToDateTimeString(date);
  };

  function renderRolloutAction(variableInfo: VariableInfo): ReactNode {
    const url = getExternalUrl(variableInfo);

    const actionButton = (
      <Button
        size="small"
        shape="circle"
        ghost
        style={{
          backgroundColor: 'transparent',
          width: 'fit-content',
        }}
        onClick={(e) => {
          e.stopPropagation();

          if (url) {
            window.open(url, '_blank');

            return false;
          }

          scribe.sendEvent(newVariablesEvents.clickVariableRollout.name, {
            STATE_NAME: newVariablesEvents.clickVariableRollout.state,
          });

          window.location.assign(`/t/variables/${variableInfo.name}/rollout`);
        }}
        icon={url ? <OpenInBrowserOutlined /> : rocketIconSvg}
      />
    );

    if (!url) return actionButton;

    return (
      <Tooltip title={EXTERNAL_PORTAL_TOOLTIP(url)}>{actionButton}</Tooltip>
    );
  }

  function renderName(variableInfo: VariableInfo): ReactNode {
    const externalUrl = getExternalUrl(variableInfo);
    const unsupportedService = getUnsupportedService(variableInfo.backend);

    return (
      <ObjectInfo
        objectType={OBJECTDETAILPAGE_OBJECTTYPE.VARIABLE}
        icon={<DataTypeIcon dataType={variableInfo.type} />}
        name={variableInfo.name}
        type={variableInfo.type}
        description={variableInfo.desc}
        nameShowTooltipWithChildren={false}
        altNameTooltip={externalUrl ? EXTERNAL_PORTAL_TOOLTIP(externalUrl) : ''}
        openUrl={externalUrl ? externalUrl : ''}
        headerTag={{
          title: unsupportedService
            ? UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY_MAP[unsupportedService]
            : '',
          bordered: false,
          color: 'blue',
        }}
      />
    );
  }

  const getColumns = () => {
    const cachedColumnsArr = tableCustomization;

    if (tableCustomization) {
      if (Array.isArray(tableCustomization)) {
        const productEntityCol = cachedColumnsArr.filter(
          (col) => col?.key === 'productEntity',
        );

        if (!productEntityCol.length) setTableCustomization(null);
      }

      if (typeof tableCustomization === 'string') {
        const tableCustomizationMap = new Map(JSON.parse(tableCustomization));

        if (!tableCustomizationMap.get('productEntity'))
          setTableCustomization(null);
      }
    }

    const columns: DashboardColumn[] = [
      {
        title: 'Name',
        dataIndex: 'name',
        fixed: 'left',
        key: 'name',
        sorter: true,
        width: 480,
        render: (_title: string, variableInfo: VariableInfo) =>
          renderName(variableInfo),
      },
      {
        key: 'status',
        title: (
          <Tooltip title="Status is updated every 2 hours">Status</Tooltip>
        ),
        width: 180,
        ellipsis: true,
        render: (_, variableInfo) => (
          <VariableStateTag variable={variableInfo} />
        ),
      },
      {
        key: 'productEntity',
        title: 'Product Name',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo: VariableInfo) => (
          <Text>{variableInfo?.productEntity || <SubtractUniversal />}</Text>
        ),
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo) => (
          <Text>{variableInfo?.createdBy || <SubtractUniversal />}</Text>
        ),
      },
      {
        key: 'lastUpdatedAt',
        title: 'Last Modified',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo) => {
          const parsedDate = transformDate(variableInfo.lastUpdatedAt);

          if (parsedDate === EMPTY_TIMESTAMP) return <SubtractUniversal />;

          return (
            <Text>
              {parsedDate}{' '}
              <SecondaryText style={{ fontSize: '14px', lineHeight: '20px' }}>
                {getTimezoneDisplay()}
              </SecondaryText>
            </Text>
          );
        },
      },
      {
        key: 'lastTriggeredAt',
        title: (
          <Tooltip title="Last Triggered is updated every 2 hours">
            Last Triggered
          </Tooltip>
        ),
        width: 252,
        ellipsis: true,
        render: (_, variableInfo) => {
          if (!isProduction) return <SubtractUniversal />;

          const parsedDate = transformDate(variableInfo.lastTriggeredAt);

          if (parsedDate === EMPTY_TIMESTAMP) return <SubtractUniversal />;

          return (
            <Text>
              {parsedDate}{' '}
              <SecondaryText style={{ fontSize: '14px', lineHeight: '20px' }}>
                {getTimezoneDisplay()}
              </SecondaryText>
            </Text>
          );
        },
      },
      {
        title: 'Tech Family',
        dataIndex: 'techFamily',
        key: 'techFamily',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo: VariableInfo) => {
          return Service.cache.getTechFamilyLabel(variableInfo.techFamily) ? (
            <Text>
              {Service.cache.getTechFamilyLabel(variableInfo.techFamily)}
            </Text>
          ) : (
            <SubtractUniversal />
          );
        },
      },
      {
        title: 'Domain',
        key: 'domain',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo: VariableInfo) => {
          return Service.cache.getDomainLabel(variableInfo?.domain) ? (
            <Text>{Service.cache.getDomainLabel(variableInfo?.domain)}</Text>
          ) : (
            <SubtractUniversal />
          );
        },
      },
      {
        title: 'Primary Service',
        key: 'backend',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo: VariableInfo) => {
          return Service.cache.getBackendServiceLabel(variableInfo?.backend) ? (
            <Text>
              {Service.cache.getBackendServiceLabel(variableInfo?.backend)}
            </Text>
          ) : (
            <SubtractUniversal />
          );
        },
      },
      {
        title: 'Subscribed Services',
        key: 'subscribedServices',
        width: 252,
        ellipsis: true,
        render: (_, variableInfo: VariableInfo) => {
          const subscribedServices =
            variableInfo?.metadata?.subscribedServices || [];

          const allServices = Service.cache.getAllBackendServices();

          const serviceDetails = subscribedServices.map((service) =>
            allServices.find((s) => s.prefix === service),
          );

          return subscribedServices.length ? (
            <Flex align="center" gap={4}>
              <Typography.Text>
                {subscribedServices.filter((service) => !!service).length}
              </Typography.Text>
              <Tooltip
                styles={{ root: { maxWidth: '100%' } }}
                color="#fff"
                title={serviceDetails.map((detail) => {
                  if (!detail) return null;

                  const { prefix, title, family } = detail;

                  return (
                    <div key={prefix} style={{ marginBlock: 4 }}>
                      <Tag bordered={false}>
                        {title}
                        {family && <span>, {family}</span>}
                      </Tag>
                    </div>
                  );
                })}
              >
                <MoreInfoOutlined />
              </Tooltip>
            </Flex>
          ) : (
            <SubtractUniversal />
          );
        },
      },
      {
        key: 'complexityLevel',
        title: 'Complexity Level',
        width: 180,
        hidden: true,
        render: (_, variableInfo: VariableInfo) => {
          const level = variableInfo?.complexityLevel;
          if (!level) return <SubtractUniversal />;
          const colorMap: Record<string, 'success' | 'warning' | 'error'> = {
            healthy: 'success',
            caution: 'warning',
            severe: 'error',
          };
          const color = colorMap[level] ?? 'default';
          return (
            <Tag size="xsmall" color={color} bordered={false}>
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </Tag>
          );
        },
      },
      {
        key: 'rolloutCount',
        title: 'Rollout Target(s)',
        width: 252,
        hidden: true,
        render: (_, variableInfo) => (
          <Text>{variableInfo?.rolloutCount || <SubtractUniversal />}</Text>
        ),
      },
      {
        key: 'experimentCount',
        title: 'Experiment(s)',
        width: 252,
        hidden: true,
        render: (_, variableInfo) => (
          <Text>{variableInfo?.experimentCount || <SubtractUniversal />}</Text>
        ),
      },
      {
        title: 'Engineering Owner',
        key: 'technicalOwner',
        width: 252,
        ellipsis: true,
        hidden: true,
        render: (_, variableInfo: VariableInfo) => (
          <Text>
            {variableInfo?.metadata?.technicalOwner || <SubtractUniversal />}
          </Text>
        ),
      },
      {
        title: 'Product Owner',
        key: 'productOwner',
        width: 252,
        ellipsis: true,
        hidden: true,
        render: (_, variableInfo: VariableInfo) => (
          <Text>
            {variableInfo?.metadata?.productOwner || <SubtractUniversal />}
          </Text>
        ),
      },
      {
        key: 'action',
        fixed: 'right',
        align: 'center',
        width: 84,
        render: (_, variableInfo: VariableInfo) => {
          return (
            <Flex align="center" gap={4}>
              <VariableSettings
                key={`variable-listing-edit-variable-${variableInfo.name}`}
                type="text"
                icon={<EditableOutlined />}
                variableName={variableInfo.name}
                onArchiveOk={() => {
                  const currentTabView = extractParamValueByName(
                    location.search,
                    VARIABLES_TAB_VIEW,
                  );

                  getConfigurations(
                    currentTabView ? currentTabView : ConfigurationTabView.ALL,
                  );
                }}
                onDeleteOk={() => {
                  const currentTabView = extractParamValueByName(
                    location.search,
                    VARIABLES_TAB_VIEW,
                  );

                  getConfigurations(
                    currentTabView ? currentTabView : ConfigurationTabView.ALL,
                  );
                }}
              />
              {renderRolloutAction(variableInfo)}
            </Flex>
          );
        },
      },
    ];

    return columns;
  };

  const renderRightActions = () => {
    return [
      <AccessControlWrapper
        action={UserAction.CREATE_A_VARIABLE}
        key="product-configurations-create-variable"
      >
        {(hasAccess) => (
          <VariableSettings
            key="variable-listing-create-variable"
            disabled={!hasAccess}
            shape="round"
            variableName={''}
          >
            Create Variable
          </VariableSettings>
        )}
      </AccessControlWrapper>,
    ];
  };

  function renderAllTabContent(): ReactNode {
    if (tabView !== ConfigurationTabView.ALL) return false;

    return (
      <DashboardTable
        bordered={false}
        rowKey={(record) => record.name}
        id="product-configurations"
        filters={filters}
        ignoreFilters={[VARIABLES_PARAM_SORTBY, VARIABLES_PARAM_ORDER]}
        filtersExcludeFromCount={[
          VARIABLES_TAB_VIEW,
          VARIABLES_PARAM_STATE,
          VARIABLES_PARAM_SEARCH_KEY,
        ]}
        searchVal={searchVal}
        pageSize={pageSize}
        pageNo={pageNumber}
        sortBy={sortBy}
        order={order}
        loading={loading}
        total={total}
        columns={getColumns()}
        dataSource={data}
        onSearch={onSearch}
        rightActions={renderRightActions()}
        filterGroups={getConfigurationsFilters(filters)}
        searchPlaceholder="Search by variable name, description and product name"
        searchWidth="450px"
      />
    );
  }

  function renderMyTabContent(): ReactNode {
    if (tabView !== ConfigurationTabView.MY) return null;

    return (
      <DashboardTable
        bordered={false}
        rowKey={(record) => record.name}
        id="product-configurations"
        filters={filters}
        ignoreFilters={[VARIABLES_PARAM_SORTBY, VARIABLES_PARAM_ORDER]}
        filtersExcludeFromCount={[
          VARIABLES_TAB_VIEW,
          VARIABLES_PARAM_OWNER,
          VARIABLES_PARAM_STATE,
          VARIABLES_PARAM_SEARCH_KEY,
          VARIABLES_PARAM_MINEONLY,
        ]}
        searchVal={searchVal}
        pageSize={pageSize}
        pageNo={pageNumber}
        sortBy={sortBy}
        order={order}
        loading={loading}
        total={total}
        columns={getColumns()}
        dataSource={data}
        onSearch={onSearch}
        rightActions={renderRightActions()}
        filterGroups={getConfigurationsFilters(filters)}
        searchPlaceholder="Search by variable name, description and product name"
        searchWidth="450px"
      />
    );
  }

  function renderContent(): ReactNode {
    return (
      <Card
        className="variables-tabs-card"
        style={{
          flexGrow: 1,
        }}
        styles={{
          body: {
            paddingTop: 12,
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Tabs
          activeKey={tabView}
          className="variables-tabs"
          size="small"
          items={[
            {
              key: 'all',
              label: 'All Variables',
              children: renderAllTabContent(),
            },
            {
              key: 'my',
              label: 'My Variables',
              children: renderMyTabContent(),
            },
          ]}
          onChange={(activeKey) => {
            const payload = {
              ...filters,
            };

            payload[VARIABLES_TAB_VIEW] = activeKey;

            if (activeKey === ConfigurationTabView.MY) {
              payload[VARIABLES_PARAM_MINEONLY] = true;
            } else {
              delete payload[VARIABLES_PARAM_MINEONLY];
            }

            updateURL(`/t/variables`, payload);
          }}
        />
      </Card>
    );
  }

  return (
    <ObjectDetailPage>
      <Flex
        vertical
        gap={16}
        style={{
          height: '100%',
          padding: 24,
        }}
      >
        <Announcements id="grabx-announcements" items={ANNOUNCEMENTS} />
        <ConfigProvider
          theme={{
            components: {
              Typography: {
                titleMarginBottom: 0,
              },
            },
          }}
        >
          <Flex align="center" justify="space-between">
            <Typography.Title level={3}>Variables</Typography.Title>
            <Flex align="center" gap={8}>
              <Button
                size="small"
                id="product-configurations-constraints-search"
                key="product-configurations-constraints-search"
                onClick={(_) => {
                  scribe.sendEvent(
                    newVariablesEvents.clickSearchByConstraints.name,
                    {
                      STATE_NAME:
                        newVariablesEvents.clickSearchByConstraints.state,
                    },
                  );

                  navigateTo(`../search/variables`);
                }}
              >
                Search by Constraints
              </Button>
              <Button
                key="changelog"
                shape="round"
                size="small"
                icon={<HistoryUniversal />}
                onClick={() => {
                  navigateTo(`../changelog`);
                }}
              >
                Global Changelog
              </Button>
            </Flex>
          </Flex>
        </ConfigProvider>
        {renderContent()}
      </Flex>
    </ObjectDetailPage>
  );
};

export default Configurations;
